import { fetchFF } from 'commerce/factFinder/fetcher';
import {
  filtersMap,
  getFFNavigationParams,
  getFFSearchParams,
} from 'commerce/factFinder/filters';
import {
  ISortBy,
  adaptFFQueryParameters,
} from 'commerce/factFinder/queryParameters';
import {
  Facets,
  FactFinderApiResponse,
  FactFinderFacet,
  FactFinderSuggestions,
  GetFFProductsParams,
} from 'commerce/factFinder/types';
import { adaptProducts } from 'commerce/shop/common';
import { errorWrapper } from 'commerce/utils/network';
import { FACT_FINDER_CHANNEL } from 'src/envVariables';

export const getFactFinderProducts = (
  params?: GetFFProductsParams,
  prefetchCb?: (urlParams: URLSearchParams) => void,
) =>
  errorWrapper(getFactFinderProducts.name, () => {
    const searchParams = getFFNavigationParams(params);

    if (prefetchCb) {
      const urlParams = new URLSearchParams();

      if (params?.page && params.page !== 1) {
        urlParams.append('page', params.page.toString());
      }

      if (params?.query) {
        urlParams.append('fss', params.query);
      }

      if (params?.filters) {
        for (const [filterName, filterValues] of Object.entries(
          params.filters,
        )) {
          const values = Array.isArray(filterValues)
            ? filterValues
            : [filterValues];
          for (const value of values) {
            urlParams.append(filterName, String(value));
          }
        }
      }

      prefetchCb(urlParams);
    }

    return fetchFF<FactFinderApiResponse>(
      `/navigation/${FACT_FINDER_CHANNEL}`,
      {
        params: searchParams,
      },
    );
  });

export const getFactFinderProductsFallback = (params?: GetFFProductsParams) => {
  return errorWrapper(getFactFinderProductsFallback.name, async () => {
    const searchParams = getFFSearchParams(params);
    return fetchFF<FactFinderApiResponse>(`/search/${FACT_FINDER_CHANNEL}`, {
      params: searchParams,
    });
  });
};

export const getLongTailProducts = (params?: GetFFProductsParams) => {
  return errorWrapper(getLongTailProducts.name, async () => {
    const searchParams = getFFSearchParams(params);
    return fetchFF<FactFinderApiResponse>(`/search/${FACT_FINDER_CHANNEL}`, {
      params: searchParams,
    });
  });
};

export const getSearchSuggestedProducts = (params: {
  query: string;
  searchField?: string;
  sortBy?: ISortBy;
  page?: number;
}) => {
  return errorWrapper(getSearchSuggestedProducts.name, async () => {
    const { data } = await fetchFF<{
      suggestions: FactFinderSuggestions[];
    }>(`/suggest/${FACT_FINDER_CHANNEL}`, {
      params: {
        query: params.query,
        searchField: params.searchField || '',
        page: params.page || 1,
      },
    });

    return data.suggestions;
  });
};

export const getSearchSuggestedCategories = (query: string) => {
  return errorWrapper(getSearchSuggestedCategories.name, async () => {
    const searchParams = adaptFFQueryParameters({
      filter: [filtersMap[Facets.CATEGORY](query)],
    });

    const { data } = await fetchFF<{ facets: FactFinderFacet[] }>(
      `/navigation/category/${FACT_FINDER_CHANNEL}`,
      {
        params: searchParams,
      },
    );

    return data;
  });
};

export const getCartSuggestions = async (params: {
  referenceKeys: string[];
}) => {
  const ffResp = await fetchFF<FactFinderApiResponse>(
    `/records/${FACT_FINDER_CHANNEL}/recommendation`,
    {
      params: {
        id: params.referenceKeys.join(','),
        maxResults: 20,
      },
    },
  );

  return adaptProducts(ffResp);
};

export const getSimilarProducts = async (referenceKey: string) => {
  return fetchFF<FactFinderApiResponse>(
    `/records/${FACT_FINDER_CHANNEL}/similar`,
    {
      params: {
        id: referenceKey,
        /** Use idType parameter to determine the type of ID passed to the method.
         * Use 'productNumber' if the given ID is from the field with 'productNumber' role.
         * Use 'id' if it is from the field with 'masterId' role.
         * */
        idType: 'productNumber',
        maxResults: 10,
      },
    },
  );
};

export const getCrossSellingProducts = async (productId: number) => {
  return fetchFF<FactFinderApiResponse>(
    `/records/${FACT_FINDER_CHANNEL}/recommendation`,
    {
      params: {
        id: productId,
        idType: 'id',
        usePersonalization: true,
        maxResults: 10,
      },
    },
  );
};

export const getProductsByProductReferenceKey = (referenceKey: string) => {
  /** @todo need to optimise this request */
  /** used navigation request because records detail request
   *  doesn't return data for parent product for products with size
   */
  return errorWrapper(getProductsByProductReferenceKey.name, () =>
    fetchFF<FactFinderApiResponse>(`/navigation/${FACT_FINDER_CHANNEL}`, {
      params: {
        filter: `productReferenceKey:${referenceKey}`,
        excludeProductsNotInRange: true,
        useDeduplication: true,
      },
    }),
  );
};
